import React from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    Chip,
    Button,
    Typography,
    CircularProgress,
    Box,
    TableSortLabel,
    Stack as MuiStack,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SwapVertRoundedIcon from "@mui/icons-material/SwapVertRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import {
    getApellidos,
    formatearFechaHora,
    obtenerRolesUsuario,
    obtenerInstitucionesUsuario
} from "../utils/formatters";

const UserTable = ({
    usuarios,
    loading,
    isMobile,
    onEdit,
    onDelete,
    onAssign,
    onResetPassword,
    onViewMore,
    sort,
    onSort,
    hasActiveFilters,
    onClearFilters,
    canResetPassword = false,
}) => {

    if (loading) {
        return (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <TableContainer sx={{ borderRadius: 2, border: "1px solid rgba(15,23,42,0.08)" }}>
            <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        <SortableHeadCell
                            label="Nombres"
                            field="nombres"
                            sort={sort}
                            onSort={onSort}
                            sx={sortableHeadCellSx}
                        />
                        <SortableHeadCell
                            label="Apellidos"
                            field="apellidos"
                            sort={sort}
                            onSort={onSort}
                            sx={sortableHeadCellSx}
                        />
                        <SortableHeadCell
                            label="Fecha de registro"
                            field="created_at"
                            sort={sort}
                            onSort={onSort}
                            sx={sortableHeadCellSx}
                        />
                        <TableCell sx={sxHeadCell}>Rol</TableCell>
                        <SortableHeadCell
                            label="Institución Educativa"
                            field="institucion"
                            sort={sort}
                            onSort={onSort}
                            sx={sortableHeadCellSx}
                        />
                        <TableCell sx={sxHeadCell} align="center">Acciones</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {usuarios.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} align="center" sx={sxBodyCell}>
                                <MuiStack spacing={1} alignItems="center" sx={{ py: 2 }}>
                                    <Typography sx={sxBodyCell}>
                                        {hasActiveFilters
                                            ? "No se encontraron usuarios con los filtros actuales."
                                            : "No hay usuarios registrados."}
                                    </Typography>
                                    {hasActiveFilters && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={onClearFilters}
                                            sx={emptyActionBtnStyles}
                                        >
                                            Limpiar filtros
                                        </Button>
                                    )}
                                </MuiStack>
                            </TableCell>
                        </TableRow>
                    ) : (
                        usuarios.map((row) => {
                            const roles = obtenerRolesUsuario(row);
                            const instituciones = obtenerInstitucionesUsuario(row);

                            return (
                                <TableRow key={row.id || row.usuario} hover sx={rowHoverStyles}>
                                    <TableCell sx={sxBodyCell}>{row.nombres || row.nombre || "-"}</TableCell>
                                    <TableCell sx={sxBodyCell}>{getApellidos(row)}</TableCell>
                                    <TableCell sx={sxBodyCell}>
                                        {formatearFechaHora(row.created_at || row.fecha_registro)}
                                    </TableCell>

                                    {/* Columna de Roles */}
                                    <TableCell sx={sxBodyCell}>
                                        <ListWithMore
                                            items={roles}
                                            limit={2}
                                            onMore={() => onViewMore(`Roles de ${row.nombres}`, roles)}
                                            renderItem={(rol, i) => (
                                                <Chip
                                                    key={i}
                                                    label={rol}
                                                    size="small"
                                                    sx={chipStyles}
                                                />
                                            )}
                                        />
                                    </TableCell>

                                    {/* Columna de Instituciones */}
                                    <TableCell sx={sxBodyCell}>
                                        <ListWithMore
                                            items={instituciones}
                                            limit={2}
                                            onMore={() => onViewMore(`Instituciones de ${row.nombres}`, instituciones)}
                                            renderItem={(inst, i) => (
                                                <Typography key={i} sx={instTextStyles}>{inst}</Typography>
                                            )}
                                        />
                                    </TableCell>

                                    {/* Acciones */}
                                    <TableCell sx={sxBodyCell} align="center">
                                        <Stack direction={isMobile ? "column" : "row"} spacing={1} justifyContent="center">
                                            <ActionButton icon={<EditIcon />} label="Editar" color="#3857a6" onClick={() => onEdit(row)} isMobile={isMobile} />
                                            <ActionButton icon={<AccountTreeIcon />} label="Asignar" color="#5b6b8a" onClick={() => onAssign(row)} isMobile={isMobile} />
                                            {canResetPassword && (
                                                <ActionButton icon={null} label="Resetear clave" color="#d97706" onClick={() => onResetPassword?.(row)} isMobile={isMobile} />
                                            )}
                                            <ActionButton icon={<DeleteIcon />} label="Eliminar" color="#d9534f" onClick={() => onDelete(row)} isMobile={isMobile} />
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

/** Sub-componentes internos para limpieza **/

const ListWithMore = ({ items, limit, onMore, renderItem }) => (
    <Stack spacing={0.7}>
        {items.slice(0, limit).map(renderItem)}
        {items.length > limit && (
            <Button size="small" variant="text" onClick={onMore} sx={verMasStyles}>
                Ver más
            </Button>
        )}
        {items.length === 0 && <Typography sx={sxBodyCell}>-</Typography>}
    </Stack>
);

const ActionButton = ({ icon, label, color, onClick, isMobile }) => (
    <Button
        size="small"
        variant="contained"
        startIcon={icon || undefined}
        onClick={onClick}
        sx={{
            minWidth: isMobile ? "100%" : "auto",
            textTransform: "none",
            borderRadius: 1.5,
            fontFamily: "Montserrat-Medium",
            fontSize: "0.8rem",
            backgroundColor: color,
            boxShadow: "none",
            "&:hover": { backgroundColor: color, filter: "brightness(0.9)" },
        }}
    >
        {label}
    </Button>
);

const SortableHeadCell = ({ label, field, sort, onSort, sx = {} }) => {
    const active = sort?.orderBy === field;
    const sortIcon = !active
        ? <SwapVertRoundedIcon sx={sortIndicatorSx} />
        : sort?.orderDir === "asc"
            ? <KeyboardArrowDownRoundedIcon sx={sortIndicatorSx} />
            : <KeyboardArrowUpRoundedIcon sx={sortIndicatorSx} />;

    return (
        <TableCell sx={{ ...sxHeadCell, ...sx }} sortDirection={active ? sort.orderDir : false}>
            <TableSortLabel
                active={active}
                hideSortIcon
                direction={active ? sort.orderDir : "asc"}
                onClick={() => onSort?.(field)}
                sx={{
                    color: "inherit",
                    "&.Mui-active": { color: "inherit" },
                    "& .MuiTableSortLabel-icon": { display: "none" },
                }}
            >
                <Box sx={sortLabelContentSx}>
                    <span>{label}</span>
                    {sortIcon}
                </Box>
            </TableSortLabel>
        </TableCell>
    );
};

/** Estilos **/

const sxHeadCell = { fontFamily: "Montserrat-Bold", fontSize: "0.9rem", color: "#334155" };
const sortableHeadCellSx = { color: "#fff" };
const sxBodyCell = { fontFamily: "Montserrat-Regular", fontSize: "0.9rem", color: "#334155" };
const instTextStyles = { fontFamily: "Montserrat-Regular", fontSize: "0.88rem", color: "#334155", lineHeight: 1.35 };
const chipStyles = { fontFamily: "Montserrat-Medium", fontSize: "0.75rem", backgroundColor: "rgba(56,87,166,0.10)", color: "#2f4c98" };
const verMasStyles = { textTransform: "none", fontFamily: "Montserrat-Medium", fontSize: "0.78rem", justifyContent: "flex-start", p: 0, color: "#3857a6" };
const rowHoverStyles = { "&:last-child td": { borderBottom: 0 }, "&:hover": { backgroundColor: "rgba(3,26,127,0.03)" } };
const emptyActionBtnStyles = { textTransform: "none", fontFamily: "Montserrat-Medium" };
const sortLabelContentSx = { display: "inline-flex", alignItems: "center", gap: 0.35 };
const sortIndicatorSx = { fontSize: "1rem", color: "inherit" };

export default UserTable;
